import { useEffect, useRef, useState } from 'react'
import { audioReferenceGaps, freeReadingSource, type AudioReferenceRecord } from '../../data/audioReferenceCatalog'
import { aePronunciationSource } from '../../data/aePronunciationReferenceCatalog'
import { phonemes } from '../../data/foundations/phonemes'
import { generatedAudioStatus } from '../../data/foundations/audioManifest'

type Review = { result: 'acceptable' | 'rejected'; reason: string; reviewedAt: string; sha256: string }
const reviewKey = 'phonics-garden.american-source-qa.v1'
const reasons = ['schwa_added', 'not_isolated', 'wrong_phoneme', 'wrong_vowel', 'accent_inconsistent', 'multiple_speakers', 'too_long', 'too_short', 'poor_recording', 'other']
const ordered = [...phonemes].sort((a, b) => Number(['phoneme_t', 'phoneme_p'].includes(b.id)) - Number(['phoneme_t', 'phoneme_p'].includes(a.id)))
const readReviews = (): Record<string, Review> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(reviewKey) ?? '{}')
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return Object.fromEntries(Object.entries(parsed).filter(([, value]) => value && typeof value === 'object'
      && ['acceptable', 'rejected'].includes(value.result) && typeof value.sha256 === 'string'
      && typeof value.reason === 'string' && typeof value.reviewedAt === 'string'))
  } catch { return {} }
}

export function AmericanSourceAudioQa() {
  const [records, setRecords] = useState<AudioReferenceRecord[]>([])
  const [reviews, setReviews] = useState(readReviews)
  const [heard, setHeard] = useState<string[]>([])
  const [error, setError] = useState('')
  const [sourceErrors, setSourceErrors] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [playing, setPlaying] = useState('')
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [selectedReasons, setSelectedReasons] = useState<Record<string, string>>({})
  const activeCount = records.filter((record) => generatedAudioStatus[`${record.phonemeId}_ga`]?.sha256 === record.sha256).length
  const verifiedCount = records.filter((record) => {
    const current = generatedAudioStatus[`${record.phonemeId}_ga`]
    return current?.sha256 === record.sha256 && current.status === 'verified'
  }).length

  useEffect(() => {
    const abort = new AbortController()
    const sources = [
      { name: 'FreeReading', path: '/audio/references/freereading/manifest.json' },
      { name: 'aePronunciation', path: '/audio/references/aepronunciation/manifest.json' },
    ]
    void Promise.allSettled(sources.map(async (source) => {
      const response = await fetch(source.path, { signal: abort.signal })
      if (!response.ok) throw new Error(`来源清单暂时无法加载（HTTP ${response.status}）`)
      const manifest = await response.json() as { records?: AudioReferenceRecord[] }
      if (!Array.isArray(manifest.records) || manifest.records.some((record) => !record
        || typeof record.id !== 'string' || typeof record.sha256 !== 'string'
        || !phonemes.some((phoneme) => phoneme.id === record.phonemeId)
        || typeof record.filePath !== 'string' || !record.signal
        || !Number.isFinite(record.signal.durationMs) || !Array.isArray(record.signal.warnings))) {
        throw new Error('来源清单格式无效')
      }
      return manifest.records
    })).then((results) => {
      if (abort.signal.aborted) return
      const merged: AudioReferenceRecord[] = []
      const errors: string[] = []
      const targets = new Set<string>()
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          errors.push(`${sources[index].name}：${result.reason instanceof Error ? result.reason.message : '加载失败'}`)
          return
        }
        result.value.forEach((record) => {
          if (targets.has(record.phonemeId)) {
            errors.push(`来源目标重复：${record.phonemeId}；未重复计入覆盖率。`)
            return
          }
          targets.add(record.phonemeId)
          merged.push(record)
        })
      })
      setRecords(merged)
      setSourceErrors(errors)
      setLoading(false)
    })
    return () => { abort.abort(); audioRef.current?.pause(); audioRef.current = null }
  }, [])

  const play = (record: AudioReferenceRecord, slow = false) => {
    audioRef.current?.pause()
    const audio = new Audio(record.filePath)
    audioRef.current = audio
    audio.playbackRate = slow ? 0.75 : 1
    audio.preservesPitch = true
    setError('')
    setPlaying(record.id)
    audio.onended = () => {
      if (audioRef.current !== audio) return
      setPlaying(''); setHeard((previous) => [...new Set([...previous, record.id])])
    }
    audio.onerror = () => { if (audioRef.current === audio) { setPlaying(''); setError('录音加载失败，请重试。') } }
    void audio.play().catch(() => { if (audioRef.current === audio) { setPlaying(''); setError('播放被阻止，请再次点击试听。') } })
  }
  const review = (record: AudioReferenceRecord, result: Review['result']) => {
    if (!heard.includes(record.id)) return
    const next = { ...reviews, [record.id]: { result, reason: result === 'rejected' ? selectedReasons[record.id] ?? 'other' : '', reviewedAt: new Date().toISOString(), sha256: record.sha256 } }
    try { localStorage.setItem(reviewKey, JSON.stringify(next)); setReviews(next) } catch { setError('本机保存失败，请检查浏览器存储设置。') }
  }
  const downloadReviews = () => {
    const blob = new Blob([JSON.stringify({ scope: 'candidate review only; no promotion', reviews }, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a'); link.href = url; link.download = 'american-source-audio-qa.json'; link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return <div className="space-y-5">
    <div className="rounded-3xl bg-garden-100 p-5">
      <h2 className="text-2xl font-black">美国教学来源 · 课堂录音试听</h2>
      <p className="mt-2 font-bold">{loading ? '正在加载…' : `${records.length} / ${phonemes.length} 个目标已取得来源文件`} · {activeCount} 个已覆盖课堂播放 · {verifiedCount} 个通过正式验收</p>
      <p className="mt-2 font-bold">来源目录：37 个 FreeReading 录音与 7 个 aePronunciation 独立音素录音。实际加载与课堂覆盖数量见上方；录音仍待人工验收。</p>
      <p className="mt-3">FreeReading / Amplify（纽约）按原网页的声音和例词匹配，未标明说话人。aePronunciation / Jonathan Sande 的美式英语真人录音由原始源码明确标记为独立音素，分别对应 /ð/、/ŋ/、/ʊ/、/ɚ/、/ɛɹ/、/ɪɹ/、/ə/。</p>
      <p className="mt-2">两组来源不是同一说话人，需要检查独立音、schwa、口音与整套元音一致性。FreeReading 保留完整示范片段，可能有停顿或重复；aePronunciation 保留独立音素文件。慢速仅用于 QA，以正常速度判断发音质量。</p>
      <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold underline">
        <a href={freeReadingSource.lettersUrl} target="_blank" rel="noreferrer">单字母声音来源</a>
        <a href={freeReadingSource.combinationsUrl} target="_blank" rel="noreferrer">字母组合声音来源</a>
        <a href={freeReadingSource.termsUrl} target="_blank" rel="noreferrer">授权依据</a>
        <a href={freeReadingSource.licenseUrl} target="_blank" rel="noreferrer">CC BY-SA 3.0</a>
        <a href={aePronunciationSource.repository} target="_blank" rel="noreferrer">aePronunciation 原始仓库</a>
        <a href={aePronunciationSource.mappingEvidence} target="_blank" rel="noreferrer">独立音素对应依据</a>
        <a href={aePronunciationSource.licenseUrl} target="_blank" rel="noreferrer">Unlicense</a>
        <a href={aePronunciationSource.nativeSpeakerEvidence} target="_blank" rel="noreferrer">母语录音说明</a>
        <a href={aePronunciationSource.accentEvidence} target="_blank" rel="noreferrer">美式元音系统说明</a>
      </div>
      <p className="mt-3 text-sm">署名与许可按条目分别展示：FreeReading / Amplify Education, Inc. 使用 CC BY-SA 3.0；Jonathan Sande / aePronunciation iOS 仓库使用 Unlicense。原 MP3 完整保留；试听 WAV 转换为单声道 44.1 kHz PCM，无裁剪、无合成，不代表发布者认可本产品。</p>
      <button onClick={downloadReviews} className="mt-4 min-h-12 rounded-2xl bg-garden-800 px-5 font-bold text-white">导出本机试听记录</button>
      <p className="mt-2 text-sm">Acceptable 只保存候选评价，不是 Verify / Promote，不改变儿童课中的音频。</p>
    </div>
    {sourceErrors.map((message) => <p key={message} role="alert" className="rounded-2xl bg-amber-100 p-4">{message} 当前清单不完整，请检查来源文件；试听不会清除此提示。</p>)}
    {error && <p role="alert" className="rounded-2xl bg-amber-100 p-4">{error}</p>}
    {!loading && ordered.map((phoneme) => {
      const record = records.find((item) => item.phonemeId === phoneme.id)
      const saved = record && reviews[record.id]?.sha256 === record.sha256 ? reviews[record.id] : undefined
      const source = record?.sourceKind === 'standalone_phoneme' ? aePronunciationSource : freeReadingSource
      return <article key={phoneme.id} className="rounded-3xl bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-center gap-3"><h3 className="text-3xl font-bold">{phoneme.ipa}</h3><span className="rounded-full bg-amber-100 px-3 py-1 text-sm font-bold">{record ? 'needs_review' : 'source_missing'}</span>{phoneme.production.schwaRisk === 'high' && <span className="font-bold text-amber-800">HIGH SCHWA RISK</span>}</div>
        <p className="mt-2 break-all font-mono text-sm">{phoneme.id} · {phoneme.canonicalExample}</p>
        {record ? <>
          <p className="mt-3">来源标记：{record.sourceSpelling}{record.sourceIpa ? ` /${record.sourceIpa}/` : ''} · 例词：{record.sourceExample} · {record.sourceKind === 'standalone_phoneme' ? '独立音素录音' : '完整示范录音'} {(record.signal.durationMs / 1000).toFixed(2)} s</p>
          <p className="mt-2 text-sm">来源：{record.publisher ?? source.publisher} · 许可：<a href={record.licenseUrl ?? source.licenseUrl} target="_blank" rel="noreferrer" className="font-bold underline">{record.license ?? source.license}</a></p>
          <p className="mt-1 text-sm">说话人：{record.speaker ?? source.speaker}</p>
          {record.sourceKind === 'standalone_phoneme' && <p className="mt-1 text-sm">原始录音由来源标记为独立音素；General American 音质及儿童教学适用性仍待试听。与其他来源说话人的一致性尚未验收。</p>}
          <p className="mt-1 text-sm">{record.signal.sampleRate} Hz · {record.signal.channels} ch · {record.signal.warnings.join(', ') || '无自动信号警告（不等于发音合格）'}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => play(record)} className="min-h-12 rounded-2xl bg-garden-800 px-5 font-bold text-white">{playing === record.id ? '重新播放' : '播放来源录音'}</button>
            <button onClick={() => play(record, true)} className="min-h-12 rounded-2xl bg-garden-100 px-5 font-bold">Slow QA</button>
            <a href={record.filePath} download className="inline-flex min-h-12 items-center rounded-2xl bg-garden-100 px-5 font-bold">下载 WAV</a>
            <a href={record.sourcePage} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center px-3 font-bold underline">原始页面</a>
            {record.nativeSpeakerEvidence && <a href={record.nativeSpeakerEvidence} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center px-3 font-bold underline">母语录音依据</a>}
            {record.accentEvidence && <a href={record.accentEvidence} target="_blank" rel="noreferrer" className="inline-flex min-h-12 items-center px-3 font-bold underline">口音依据</a>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button disabled={!heard.includes(record.id)} onClick={() => review(record, 'acceptable')} className="min-h-12 rounded-2xl border border-garden-300 px-4 font-bold disabled:opacity-40">Acceptable</button>
            <select aria-label={`${phoneme.ipa} Reject reason`} className="min-h-12 max-w-full rounded-2xl bg-garden-50 px-3" value={selectedReasons[record.id] ?? 'other'} onChange={(event) => setSelectedReasons((value) => ({ ...value, [record.id]: event.target.value }))}>{reasons.map((reason) => <option key={reason}>{reason}</option>)}</select>
            <button disabled={!heard.includes(record.id)} onClick={() => review(record, 'rejected')} className="min-h-12 rounded-2xl border border-garden-300 px-4 font-bold disabled:opacity-40">Reject</button>
          </div>
          <p className="mt-2 text-sm" aria-live="polite">{saved ? `本机评价：${saved.result}${saved.reason ? ` · ${saved.reason}` : ''}` : '完整试听后可保存评价；结果只存本机。'}</p>
        </> : <p className="mt-3 text-garden-700">来源清单尚未加载该文件。{audioReferenceGaps[phoneme.id] ? `原 FreeReading 来源限制：${audioReferenceGaps[phoneme.id]}` : '请检查文件下载与来源清单。'}</p>}
      </article>
    })}
  </div>
}
